import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/** Prix service explicite (0 affiché à part ; absent = null/undefined/vide). */
export function isExplicitServicePrice(price: unknown): price is number {
  if (price === null || price === undefined) return false;
  if (typeof price === 'string' && price.trim() === '') return false;
  return typeof price === 'number' && !Number.isNaN(price);
}

export type FormatServicePriceOptions = {
  decimals?: number;
  currencySuffix?: string;
};

/**
 * Affiche un tarif service : 0 → libellé gratuit (i18n), absent → null, sinon montant + €.
 */
export function formatServicePrice(
  price: unknown,
  freeLabel: string,
  options?: FormatServicePriceOptions
): string | null {
  if (!isExplicitServicePrice(price)) return null;
  if (price === 0) return freeLabel;
  const suffix = options?.currencySuffix ?? '€';
  if (options?.decimals != null) return `${price.toFixed(options.decimals)}${suffix}`;
  return `${price}${suffix}`;
}

/** Total résa (prestation + déplacement) : 0 € → libellé gratuit. */
export function formatServiceTotalPrice(
  servicePrice: unknown,
  travelFeeEur: number | null | undefined,
  freeLabel: string
): string {
  const travel = travelFeeEur ?? 0;
  if (!isExplicitServicePrice(servicePrice)) {
    if (travel === 0) return '';
    return `${travel.toFixed(2)}€`;
  }
  const total = servicePrice + travel;
  if (total === 0) return freeLabel;
  return `${total.toFixed(2)}€`;
}

export function useFormatServicePrice() {
  const { t } = useTranslation('common');
  const freeLabel = t('servicePrice.free');

  const formatPrice = useCallback(
    (price: unknown, options?: FormatServicePriceOptions) =>
      formatServicePrice(price, freeLabel, options),
    [freeLabel]
  );

  const formatTotal = useCallback(
    (servicePrice: unknown, travelFeeEur: number | null | undefined) =>
      formatServiceTotalPrice(servicePrice, travelFeeEur, freeLabel),
    [freeLabel]
  );

  return { formatPrice, formatTotal, freeLabel };
}
